import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, firstValueFrom, map, Observable } from 'rxjs';

import { captureException } from '@sentry/angular';
import * as crypto from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import { filterNil } from '@ngneat/elf';
import { InstitutionPermissionState } from '../models/user';

import {
  BoardFolder,
  Frame,
  FrameItem,
  FramePrivacy,
  FrameType,
  Room,
  ROOT_BOARD_FOLDER_NAME,
} from '../models/session';
import { SpaceRepository } from '../state/space.repository';
import { isValidYCanvasItem, YCanvasItemObject, YObject } from '../sessions/common/y-session';
import {
  YOperationType,
  YSessionBoardItemsAdapter,
} from '../sessions/common/y-session-board-items-adapter.service';
import {
  BoardOperationsService,
  scrollFramesTrayToBottom,
} from '../sessions/session/services/board-operations.service';
import { SUCCESSES } from '../common/utils/notification-constants';
import { ItemModel } from '../models/itemModel';
import { downloadBlob } from '../utilities/FileUtils';
import { RealtimeSpaceService } from './realtime-space.service';
import { SpaceBoardsService } from './space-boards.service';
import { NotificationToasterService } from './notification-toaster.service';
import { SpacesService } from './spaces.service';
import { SessionsSyncService } from './sessions-sync.service';
import { UserService } from './user.service';
import { FeatureManagementService } from './feature-management.service';

interface BoardFileSource {
  sessionId: string;
  roomId: string;
  frameUid: string;
}

interface SessionContext {
  sessionId: string;
  roomId: string;
}

interface BoardFileFrame {
  name?: string;
  frameType?: FrameType;
  backgroundPattern?: unknown;
  backgroundColor?: unknown;
  privacy?: FramePrivacy;
  privateUsers?: string[];
  boardFolderUid?: string;
}

type BoardFileObject = YCanvasItemObject | YObject | Record<string, unknown>;

interface BoardFilePayload {
  createdAt: number;
  source: BoardFileSource;
  frame: BoardFileFrame;
  objects: BoardFileObject[];
}

interface SpaceFileFrame {
  frame: BoardFileFrame;
  objects: BoardFileObject[];
}

interface SpaceFileRoom {
  roomUid?: string;
  name?: string;
  frames: SpaceFileFrame[];
  boardFolders: Array<{ uid?: string; name: string }>;
}

interface SpaceFilePayload {
  isSpaceFile: boolean;
  createdAt: number;
  source: { sessionId: string };
  rooms: SpaceFileRoom[];
}

@Injectable({ providedIn: 'root' })
export class BoardCloneService {
  public readonly fileExtension = '.space';
  private readonly xorKey = 0xa5;
  private readonly octetStreamMime = 'application/octet-stream';
  // Not recommended to have this in front-end we should have the encryption server-mediated,
  // but we are currently aiming to just make reading the content hard, not preventing it at all
  private readonly SECRET_KEY = '0dksal;fj@?Qfeadkml';

  private readonly textEncoder = new TextEncoder();
  private readonly textDecoder = new TextDecoder();

  public readonly isSpaceFileExportAllowed$: Observable<boolean>;

  constructor(
    private spaceRepo: SpaceRepository,
    private translate: TranslateService,
    private ySessionAdapter: YSessionBoardItemsAdapter,
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceBoardsService: SpaceBoardsService,
    private notificationToasterService: NotificationToasterService,
    private boardOperationsService: BoardOperationsService,
    private spacesService: SpacesService,
    private sessionsSyncService: SessionsSyncService,
    private userService: UserService,
    private featureManagementService: FeatureManagementService,
  ) {
    this.isSpaceFileExportAllowed$ = combineLatest([
      this.spaceRepo.activeSpace$.pipe(filterNil()),
      this.featureManagementService.isExportImportSpaceFileEnabled$,
    ]).pipe(
      map(
        ([session, capabilityEnabled]) =>
          capabilityEnabled &&
          session.institution?.permissions?.downloadSpacesOrBoards !==
            InstitutionPermissionState.ALWAYS_OFF,
      ),
      distinctUntilChanged(),
    );
  }

  public async exportFrameToFile(frame: Frame): Promise<void> {
    const activeSessionContext = this.getActiveSessionContext();
    if (!activeSessionContext || !frame?.uid) {
      return;
    }

    const { objects } = await firstValueFrom(
      this.spaceRepo.boardItems$(activeSessionContext.sessionId, frame.uid),
    );

    const serializableObjects = this.serializeBoardItemsForExport(
      Array.isArray(objects) ? (objects as YObject[]) : [],
    );

    const payload = this.buildFrameExportPayload(frame, activeSessionContext, serializableObjects);

    const blob = await this.encodePayloadToBlob(payload);

    const fileName = this.buildBoardFileName(frame.name);

    downloadBlob(blob, fileName);
  }

  /**
   * Import a .board file and create a new frame with its contents.
   */
  public importFrameFromDecodedPayload(
    boardFilePayload: BoardFilePayload | SpaceFileFrame,
    activeSessionContext: SessionContext,
    targetRoomId?: string,
    foldersMap?: Map<string, string>,
  ): Frame {
    targetRoomId = targetRoomId ?? activeSessionContext.roomId;

    const newFrame = this.buildFrameFromPayload(boardFilePayload, foldersMap);
    this.realtimeSpaceService.service.addFrame(
      newFrame,
      activeSessionContext.sessionId,
      targetRoomId,
    );
    this.addObjectsToFrame(boardFilePayload.objects, newFrame, activeSessionContext.sessionId);
    return newFrame;
  }

  /**
   * Export the whole active space as a .space file using YJS JSON dump
   */
  public async exportActiveSpaceToFile(): Promise<void> {
    const activeSpace = this.spaceRepo.activeSpace;
    if (!activeSpace?._id) {
      return;
    }
    const payload = await this.buildSpaceExportPayloadFromDump(activeSpace._id);

    const blob = await this.encodePayloadToBlob(payload);

    const fileName = this.buildSpaceFileName(activeSpace.title);

    downloadBlob(blob, fileName);
  }

  private async importSpaceFromDecodedPayload(
    payload: SpaceFilePayload,
    activeSessionContext: SessionContext,
  ): Promise<{ boards: number; rooms: number }> {
    // Step 1: Create rooms in backend database and build mapping (outside transaction)
    const { roomIdMapping, createdRoomObjects } = await this.createBackendRoomsAndBuildMapping(
      activeSessionContext.sessionId,
      activeSessionContext.roomId,
      payload.rooms,
    );

    // Number of created rooms = total rooms - 1 (main room content goes to current room)
    const createdRoomsCount = payload.rooms.length - 1;

    let createdBoardsCount = 0;

    // Step 2: Single synchronous transaction for all Y doc modifications
    this.sessionsSyncService
      .getYSessionById(activeSessionContext.sessionId)
      ?.getYDoc()
      .transact(() => {
        // Add created rooms to Y doc
        if (createdRoomObjects.length > 0) {
          this.realtimeSpaceService.service.addRooms(
            activeSessionContext.sessionId,
            createdRoomObjects,
          );
        }

        // Create folders in Y doc and build mappings for all rooms
        const folderMapsPerRoom = this.createFoldersAndBuildRoomsMappings(
          activeSessionContext.sessionId,
          payload.rooms,
          roomIdMapping,
        );

        // Add frames to all rooms
        for (const room of payload.rooms) {
          const roomKey = room.roomUid || room.name || '';
          const targetRoomId = roomIdMapping.get(roomKey);
          if (!targetRoomId) {
            continue;
          }

          const folderMap = folderMapsPerRoom.get(roomKey);

          createdBoardsCount += this.addFramesToRoom(
            activeSessionContext,
            targetRoomId,
            room,
            folderMap,
          );
        }
      });

    return { boards: createdBoardsCount, rooms: createdRoomsCount };
  }

  private isMainRoomUid(roomUid?: string): boolean {
    return typeof roomUid === 'string' && roomUid.endsWith(':MainRoom');
  }

  /**
   * Creates all non-main rooms in the backend database and builds a mapping from imported room identifiers to new room IDs.
   *
   * This method performs two key operations:
   * 1. Creates rooms in the backend database via batch API call (does NOT add to Y doc yet)
   * 2. Builds a mapping between the imported space's room identifiers (roomUid or name) to the newly created room IDs
   *
   * For main rooms (identified by ':MainRoom' suffix), maps to the current active room ID instead of creating new rooms.
   *
   * @returns Object containing:
   *   - roomIdMapping: Map from imported room identifier to new/current room ID
   *   - createdRoomObjects: Array of Room objects created in backend (to be added to Y doc in transaction)
   */
  private async createBackendRoomsAndBuildMapping(
    sessionId: string,
    currentRoomId: string,
    rooms: SpaceFileRoom[],
  ): Promise<{ roomIdMapping: Map<string, string>; createdRoomObjects: Room[] }> {
    const roomIdMapping = new Map<string, string>();
    const roomsToCreate: SpaceFileRoom[] = [];

    // Separate main room from rooms to create
    for (const room of rooms) {
      const roomKey = room.roomUid || room.name || '';
      if (this.isMainRoomUid(room.roomUid)) {
        // Main room: map to current active room instead of creating new one
        roomIdMapping.set(roomKey, currentRoomId);
      } else {
        // Non-main room: needs to be created in backend
        roomsToCreate.push(room);
      }
    }

    // Batch create all non-main rooms in backend database
    let createdRoomObjects: Room[] = [];
    if (roomsToCreate.length > 0) {
      createdRoomObjects = await this.createMultipleRooms(sessionId, roomsToCreate);

      // Build mapping from imported room identifier to newly created room ID
      roomsToCreate.forEach((room, index) => {
        const roomKey = room.roomUid || room.name || '';
        roomIdMapping.set(roomKey, createdRoomObjects[index].uid);
      });
    }

    return { roomIdMapping, createdRoomObjects };
  }

  /**
   * Creates multiple rooms in a single batch API call.
   * Returns the Room objects with their generated UIDs.
   * Note: Does NOT add rooms to Y doc - that should be done in the transaction.
   */
  private async createMultipleRooms(sessionId: string, rooms: SpaceFileRoom[]): Promise<Room[]> {
    const roomObjects = rooms.map((room) => new Room({ name: room.name }));

    await firstValueFrom(this.spacesService.createBreakoutRoom(sessionId, roomObjects));

    return roomObjects;
  }

  /**
   * Creates board folders in the Y doc and builds mappings from imported folder identifiers to new folder UIDs for all rooms.
   *
   * This method processes each room and:
   * 1. Creates new BoardFolder objects in the Y doc via realtimeSpaceService
   * 2. Builds a mapping between the imported space's folder UIDs to the newly created folder UIDs
   *
   * @param sessionId - The current space/session ID
   * @param rooms - Array of rooms from the imported space file
   * @param roomIdMapping - Map from imported room identifier to new/current room ID
   * @returns Map of room identifier to folder mapping (old folder UID -> new folder UID)
   */
  private createFoldersAndBuildRoomsMappings(
    sessionId: string,
    rooms: SpaceFileRoom[],
    roomIdMapping: Map<string, string>,
  ): Map<string, Map<string, string>> {
    const folderMapsPerRoom = new Map<string, Map<string, string>>();

    for (const room of rooms) {
      const roomKey = room.roomUid || room.name || '';
      const targetRoomId = roomIdMapping.get(roomKey);

      if (targetRoomId) {
        // Create folders in Y doc and build mapping for this room
        const folderMap = this.createFolderMapForRoom(sessionId, targetRoomId, room);
        folderMapsPerRoom.set(roomKey, folderMap);
      }
    }

    return folderMapsPerRoom;
  }

  private createFolderMapForRoom(
    sessionId: string,
    targetRoomId: string,
    room: SpaceFileRoom,
  ): Map<string, string> {
    const folderMap = new Map<string, string>();
    for (const folder of room.boardFolders || []) {
      const newFolder = new BoardFolder({ name: folder.name, roomUid: targetRoomId });
      this.realtimeSpaceService.service.addBoardFolder(sessionId, newFolder);
      folderMap.set(folder.uid || folder.name, newFolder.uid);
    }
    return folderMap;
  }

  private buildFrameFromPayload(
    payload: BoardFilePayload | SpaceFileFrame,
    folderMap?: Map<string, string>,
  ): Frame {
    const frameProps = payload.frame || {};
    const boardName =
      frameProps?.name ||
      `${this.translate.instant('Board')} ${
        this.spaceBoardsService.activeSpaceCurrentRoomFrames.length + 1
      }`;

    return new Frame({
      name: boardName,
      frameType: frameProps?.frameType ?? FrameType.WHITEBOARD,
      backgroundPattern: frameProps?.backgroundPattern,
      backgroundColor: frameProps?.backgroundColor,
      privacy: frameProps?.privacy as FramePrivacy | undefined,
      privateUsers: frameProps?.privateUsers,
      boardFolderUid:
        (folderMap
          ? this.resolveFolderUid(folderMap, frameProps?.boardFolderUid)
          : this.spaceRepo.activeSpace?.selectedFolderBoardUid) ?? ROOT_BOARD_FOLDER_NAME,
    });
  }

  private addFramesToRoom(
    activeSessionContext: SessionContext,
    targetRoomId: string,
    room: SpaceFileRoom,
    folderMap?: Map<string, string>,
  ): number {
    let framesCount = 0;
    for (const frameEntry of room.frames || []) {
      this.importFrameFromDecodedPayload(frameEntry, activeSessionContext, targetRoomId, folderMap);

      framesCount += 1;
    }
    return framesCount;
  }

  /**
   * Unified export method that exports either a specific board (when frame is provided)
   * or the active space (when no frame is provided). Handles success/error via existing
   * underlying methods which already show notifications.
   */
  public async exportSpaceOrBoardToFile(frame?: Frame): Promise<void> {
    try {
      if (frame) {
        await this.exportFrameToFile(frame);
        this.notificationToasterService.showDefaultSuccessNotification(
          this.translate.instant('Board downloaded as .space file'),
          SUCCESSES.BOARD_EXPORTED_AS_FILE,
        );
        return;
      }

      await this.exportActiveSpaceToFile();
      this.notificationToasterService.showDefaultSuccessNotification(
        this.translate.instant('Space successfully downloaded!'),
        SUCCESSES.BOARD_EXPORTED_AS_FILE,
      );
    } catch (e) {
      captureException(e);
      this.showDefaultError(
        this.translate.instant('Download failed'),
        this.translate.instant('Please try again or contact support if the issue persists.'),
      );
    }
  }

  /**
   * Auto-detects file type and imports a space (.space) or a single board (.board)
   */
  public async importSpaceOrBoardFromFile(
    file: File,
    suppressNotification = false,
  ): Promise<Frame | undefined> {
    try {
      const activeSessionContext = this.getActiveSessionContext();
      if (!activeSessionContext) {
        return;
      }

      const decoded = await this.decodePayloadFromFile(file);

      if (this.isSpaceFilePayload(decoded)) {
        const { boards, rooms } = await this.importSpaceFromDecodedPayload(
          decoded,
          activeSessionContext,
        );
        !suppressNotification && this.notifyBoardsOrSpaceImported(boards, rooms);
        return;
      }

      if (this.isBoardFilePayload(decoded)) {
        const frame = await this.importFrameFromDecodedPayload(decoded, activeSessionContext);
        !suppressNotification && this.notifyBoardImported(frame);
        return frame;
      }

      throw new Error('Invalid file payload');
    } catch (e) {
      captureException(e);
      this.showDefaultError(
        this.translate.instant('Upload failed'),
        this.translate.instant(
          'Failed to upload file. please try again or contact support if the issue persists',
        ),
      );
    }
  }

  private async buildSpaceExportPayloadFromDump(spaceId: string): Promise<SpaceFilePayload> {
    const yDump: any = (this.realtimeSpaceService.service as any).sessionToJSON(spaceId);
    const root = yDump?.sessionJSON ?? yDump;

    const rooms: Array<{ uid: string; name: string }> = Array.isArray(root?.rooms)
      ? root.rooms
      : [];

    const exportRooms = rooms.map((room) => {
      const framesKey = `frames${room.uid}`;
      const yFrames: any[] = Array.isArray(root?.[framesKey]) ? root[framesKey] : [];

      const exportFrames = yFrames.map((fr) => {
        const fabricItems: any[] = Array.isArray(root?.[fr.uid]) ? root[fr.uid] : [];
        const canvasItems: any[] = Array.isArray(root?.[`canvas-items-${fr.uid}`])
          ? root[`canvas-items-${fr.uid}`]
          : [];
        const serializableObjects = this.serializeBoardItemsForExport(
          ([] as any[]).concat(fabricItems, canvasItems),
        );
        return {
          frame: {
            name: fr?.name,
            frameType: fr?.frameType,
            backgroundPattern: fr?.backgroundPattern,
            backgroundColor: fr?.backgroundColor,
            privacy: fr?.privacy,
            privateUsers: fr?.privateUsers,
            boardFolderUid: fr?.boardFolderUid,
          },
          objects: serializableObjects,
        } as SpaceFileFrame;
      });

      const allFolders: any[] = Array.isArray(root?.boardFolders) ? root.boardFolders : [];
      const roomFolders = allFolders
        .filter((f) => f?.roomUid === room.uid)
        .map((f) => ({ uid: f?.uid, name: f?.name }));

      return {
        roomUid: room.uid,
        name: room.name,
        frames: exportFrames,
        boardFolders: roomFolders,
      } as SpaceFileRoom;
    });

    return {
      isSpaceFile: true,
      createdAt: Date.now(),
      source: { sessionId: spaceId },
      rooms: exportRooms,
    };
  }

  private resolveFolderUid(folderMap: Map<string, string>, original?: string): string | undefined {
    if (!original) {
      return undefined;
    }
    return folderMap.get(original) || folderMap.get(ROOT_BOARD_FOLDER_NAME) || undefined;
  }

  private async encodePayloadToBlob<T>(payload: T): Promise<Blob> {
    const json = JSON.stringify(payload);
    const encryptedJson = await this.encryptData(json);
    const data = this.textEncoder.encode(encryptedJson);
    // Lightweight obfuscation: XOR
    const encodedData = this.xor(data, this.xorKey);

    const encodedDataBytes = new Uint8Array(encodedData.length);
    encodedDataBytes.set(encodedData, 0);

    return new Blob([encodedDataBytes.buffer], { type: this.octetStreamMime });
  }

  private async decodePayloadFromFile(file: File): Promise<BoardFilePayload | undefined> {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const data = this.xor(bytes, this.xorKey);
    const encryptedJson = this.textDecoder.decode(data);
    const json = await this.decryptData(encryptedJson);
    return JSON.parse(json);
  }

  private xor(data: Uint8Array, key: number): Uint8Array {
    const out = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      // eslint-disable-next-line no-bitwise
      out[i] = data[i] ^ key;
    }
    return out;
  }

  private buildBoardFileName(boardName: string): string {
    const base = boardName || this.translate.instant('Board');
    return `${base} - Board${this.fileExtension}`;
  }

  private buildSpaceFileName(spaceTitle: string): string {
    const base = spaceTitle || this.translate.instant('Space');
    return `${base} - Space${this.fileExtension}`;
  }

  private serializeBoardItemsForExport(
    objects: (YCanvasItemObject | YObject)[],
  ): BoardFileObject[] {
    return objects.map((obj) => {
      if (isValidYCanvasItem(obj)) {
        const cloned: Record<string, unknown> = { ...(obj as unknown as Record<string, unknown>) };
        const pos = (obj as unknown as { position?: unknown }).position;
        if (pos && pos instanceof Map) {
          // We can not serialize Maps directly we need to convert them to plain objects first
          cloned.position = Object.fromEntries((pos as Map<string, string>).entries());
        }
        return cloned;
      }
      return obj;
    });
  }

  private reviveBoardItemsFromImport(
    objects: (YCanvasItemObject | YObject | Record<string, unknown>)[],
  ): (YCanvasItemObject | YObject)[] {
    return objects.map((obj) => {
      if (isValidYCanvasItem(obj as YCanvasItemObject | YObject)) {
        const cloned: Record<string, unknown> = { ...(obj as Record<string, unknown>) };
        const pos = (cloned as { position?: unknown }).position;
        if (pos && !(pos instanceof Map) && typeof pos === 'object') {
          cloned.position = new Map<string, string>(Object.entries(pos as Record<string, string>));
        }
        return cloned as unknown as YCanvasItemObject | YObject;
      }
      return obj as YCanvasItemObject | YObject;
    });
  }

  /**
   * Clones a plain YObject or YCanvasItemObject for a new board without using Fabric enlivening.
   * This is a synchronous operation that updates necessary IDs and properties.
   */
  private clonePlainObjectForNewBoard(
    object: YCanvasItemObject | YObject,
    boardId: string,
  ): YCanvasItemObject | YObject {
    // Use any to avoid type issues with configurations property
    const cloned: any = { ...object };

    cloned.boardUid = boardId;
    cloned.uid = uuidv4();
    cloned.vuid = uuidv4();

    // So that the web viewers are not connected,
    // The web viewer handles the undefined values and creates a new session for the copied web viewer
    const frameItem: FrameItem = cloned as FrameItem;
    if (frameItem.browser_state) {
      frameItem.browser_state.hbSessionId = undefined;
      frameItem.browser_state.url = undefined;
      frameItem.browser_state.remoteControlToken = undefined;
    }

    // Update ownership for Mario apps so the importing user can configure them
    // And change the mario id to get a new instance of the mario
    if (frameItem.model === ItemModel.Mario && frameItem.mario_state) {
      frameItem.ownerId = this.userService.userId;
      frameItem.mario_state.id = uuidv4();
    }

    return cloned;
  }

  private addObjectsToFrame(
    objects: (YCanvasItemObject | YObject | Record<string, unknown>)[] | undefined,
    newFrame: Frame,
    sessionId: string,
  ): void {
    const boardItems: (YCanvasItemObject | YObject)[] = Array.isArray(objects)
      ? this.reviveBoardItemsFromImport(objects)
      : [];

    if (boardItems.length === 0) {
      return;
    }

    // Clone plain objects directly without Fabric enlivening
    const clonedObjects = boardItems.map((obj) =>
      this.clonePlainObjectForNewBoard(obj, newFrame.uid),
    );

    // Separate into YObjects and YCanvasItemObjects
    const yObjects: YObject[] = [];
    const yCanvasItems: YCanvasItemObject[] = [];

    for (const obj of clonedObjects) {
      if (isValidYCanvasItem(obj)) {
        yCanvasItems.push(obj as YCanvasItemObject);
      } else {
        yObjects.push(obj as YObject);
      }
    }

    // Create maps for Y session update
    const updatedFabricObjects = new Map<string | undefined, YObject[]>();
    const updatedCanvasItems = new Map<string | undefined, YCanvasItemObject[]>();

    if (yObjects.length > 0) {
      updatedFabricObjects.set(newFrame.uid, yObjects);
    }

    if (yCanvasItems.length > 0) {
      updatedCanvasItems.set(newFrame.uid, yCanvasItems);
    }

    // Add directly to Y session using the plain objects method
    this.ySessionAdapter.addPlainObjectsToYSession(
      sessionId,
      updatedFabricObjects,
      updatedCanvasItems,
      YOperationType.Add,
    );
  }

  public notifyBoardImported(newFrame: Frame): void {
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translate.instant('Board successfully uploaded!'),
      SUCCESSES.BOARD_IMPORTED,
      false,
      '',
      (builder) =>
        builder.actionButtonsConfig({
          primary: {
            text: this.translate.instant('Go to board'),
            onClick: () => {
              this.boardOperationsService.selectFrame(newFrame.uid);
              scrollFramesTrayToBottom();
            },
            close: true,
          },
        }),
    );
  }

  public notifyBoardsOrSpaceImported(boards?: number, rooms?: number): void {
    let message;
    if (rooms !== undefined && boards !== undefined) {
      const boardsLabel = this.translate.instant(`board${boards === 1 ? '' : 's'}`);
      const roomsLabel = this.translate.instant(`Room${rooms === 1 ? '' : 's'}`);
      message = this.translate.instant(
        `Uploaded ${boards} ${boardsLabel} and ${rooms} ${roomsLabel}.`,
      );
    } else {
      message = this.translate.instant('Boards successfully imported!');
    }

    this.notificationToasterService.showDefaultSuccessNotification(
      message,
      SUCCESSES.BOARD_IMPORTED,
      false,
      '',
      (builder) =>
        builder.actionButtonsConfig({
          primary: {
            text: this.translate.instant('Open boards manager'),
            onClick: () => {
              this.boardOperationsService.openBoardsTab({
                preventCloseAllBoardsWhenItsOpen: true,
              });
              scrollFramesTrayToBottom();
            },
            close: true,
          },
        }),
    );
  }

  private getActiveSessionContext(): SessionContext | null {
    const sessionId = this.spaceRepo.activeSpace?._id;
    const roomId = this.spaceRepo.activeSpaceCurrentRoomUid;
    return sessionId && roomId ? { sessionId, roomId } : null;
  }

  private buildFrameExportPayload(
    frame: Frame,
    ctx: { sessionId: string; roomId: string },
    serializableObjects: BoardFileObject[],
  ): BoardFilePayload {
    const copiedName = `${frame?.name} ${this.translate.instant('copy')}`;

    return {
      createdAt: Date.now(),
      source: { sessionId: ctx.sessionId, roomId: ctx.roomId, frameUid: frame.uid },
      frame: {
        name: copiedName,
        frameType: frame?.frameType ?? FrameType.WHITEBOARD,
        backgroundPattern: frame?.backgroundPattern,
        backgroundColor: frame?.backgroundColor,
        privacy: frame?.privacy,
        privateUsers: frame?.privateUsers,
        boardFolderUid: frame?.boardFolderUid,
      },
      objects: serializableObjects,
    };
  }

  private showDefaultError(title: string, description: string): void {
    this.notificationToasterService.showDefaultErrorNotificationV2(
      title,
      description,
      this.notificationToasterService.generateNotificationCode(),
    );
  }

  private isBoardFilePayload(value: unknown): value is BoardFilePayload {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const v = value as Partial<BoardFilePayload> & { [key: string]: unknown };
    const src = v.source as Partial<BoardFileSource> | undefined;
    const frm = v.frame as Partial<BoardFileFrame> | undefined;
    return (
      typeof v.createdAt === 'number' &&
      !!src &&
      typeof src.sessionId === 'string' &&
      typeof src.roomId === 'string' &&
      typeof src.frameUid === 'string' &&
      !!frm &&
      typeof frm === 'object' &&
      Array.isArray(v.objects)
    );
  }

  // Encrypt any JS object
  async encryptData(data: string) {
    return (await crypto.AES.encrypt(data, this.SECRET_KEY)).toString();
  }

  // Decrypt back into object
  async decryptData(ciphertext) {
    const bytes = await crypto.AES.decrypt(ciphertext, this.SECRET_KEY);
    return bytes.toString(crypto.enc.Utf8);
  }

  public isBoardFile(file?: any): file is File {
    return (
      file &&
      file instanceof File &&
      Boolean(file?.name?.toLowerCase().endsWith(this.fileExtension))
    );
  }

  /**
   * Detects whether a PNCL file is a space file by decoding its payload.
   * Returns true if it's a valid space payload; false otherwise.
   */
  public async isSpaceFile(file: File): Promise<boolean> {
    try {
      const decoded = await this.decodePayloadFromFile(file);
      return this.isSpaceFilePayload(decoded);
    } catch {
      return false;
    }
  }

  private isSpaceFilePayload(value: unknown): value is SpaceFilePayload {
    if (!this.isPlainObject(value)) {
      return false;
    }
    const v = value as Record<string, unknown>;
    if (v.isSpaceFile !== true) {
      return false;
    }
    if (typeof v.createdAt !== 'number') {
      return false;
    }
    if (!this.isPlainObject(v.source) || !this.isString((v.source as any).sessionId)) {
      return false;
    }
    if (!Array.isArray(v.rooms)) {
      return false;
    }
    return (v.rooms as unknown[]).every((room) => {
      if (!this.isPlainObject(room)) {
        return false;
      }
      const r = room as Record<string, unknown>;
      if (r.roomUid !== undefined && !this.isString(r.roomUid)) {
        return false;
      }
      if (!this.isString(r.name)) {
        return false;
      }
      if (!Array.isArray(r.frames) || !Array.isArray(r.boardFolders)) {
        return false;
      }
      const framesOk = (r.frames as unknown[]).every(
        (fr) =>
          this.isPlainObject(fr) &&
          this.isPlainObject((fr as any).frame) &&
          Array.isArray((fr as any).objects),
      );
      const foldersOk = (r.boardFolders as unknown[]).every(
        (f) =>
          this.isPlainObject(f) &&
          ((f as any).uid === undefined || this.isString((f as any).uid)) &&
          this.isString((f as any).name),
      );
      return framesOk && foldersOk;
    });
  }

  // Removed deep meta validation; we generate payloads and only require structural shape here.

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isString(value: unknown): value is string {
    return typeof value === 'string';
  }
}
